import React from 'react';
import { Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Link } = Typography;

/** Server-referenced document/attachment — never fabricate; display reference only. */
export interface DocumentPanelEntryV0 {
  readonly id: string;
  readonly label: string;
  readonly kind?: string | null;
  readonly href?: string | null;
}

export interface DocumentPanelProps {
  readonly presentationState: PrimitivePresentationState;
  readonly documents?: readonly DocumentPanelEntryV0[];
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

/**
 * DocumentPanel (Family B) — displays server-referenced document/attachment
 * links only. This is a display panel, not an evidence-sufficiency primitive:
 * it never claims a listed document is admissible or sufficient evidence
 * (that distinction remains EvidenceSummary's exclusive responsibility).
 */
export function DocumentPanel(props: DocumentPanelProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('DocumentPanel must keep grantsAuthority/grantsApproval=false');
  }

  const documents = props.documents ?? [];

  return (
    <PrimitiveSurfaceFrame
      title="문서"
      presentationState={props.presentationState}
      emptyMessage="연결된 문서가 없습니다."
      data-testid={props['data-testid'] ?? 'document-panel'}
      aria-label="문서 패널"
    >
      {documents.length === 0 ? (
        <Text type="secondary">서버가 제공한 문서가 없습니다.</Text>
      ) : (
        <div className={styles.primitiveStack}>
          {documents.map((doc) => (
            <div key={doc.id} className={styles.primitiveFieldRow} data-testid="document-panel-row">
              <span className={styles.primitiveFieldLabel}>
                {doc.kind?.trim() ? doc.kind : '문서'}
              </span>
              {doc.href ? <Link href={doc.href}>{doc.label}</Link> : <Text>{doc.label}</Text>}
            </div>
          ))}
        </div>
      )}
    </PrimitiveSurfaceFrame>
  );
}
